import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Select from 'react-select';
import { EmpresaTenant, FechamentoEvento, FechamentoEventoStatus, JustificativaTemplate, ManualEntry, MatrizContaContabil, MOTIVOS_DEVOLUCAO, MonitoramentoItem, MotivoDevolucao, PdcaConteudo, PendenciasTransversaisResponse, PlanoMelhoria, PlanoMelhoriaAcaoInput, RiskLevel, SoxEvent, WorkflowConciliacao, WorkflowStatus } from './models';
import { eventosDePrazos } from './calendarioEventos';
import { extrairMensagemErro } from './mensagemErro';
import { formatarDuracao, tempoMedioAprovacaoHoras } from './tempoMedio';
import workflowService from './workflowService';
import { aprovacoesPorNivelDaApi, montarFunil, rotuloAprovacoes } from './funilWorkflow';
import { abrirCapaParaImpressao } from './imprimirCapa';
import { resolverDestinoCockpit, IndicadorCockpit, ContextoCockpit, DadosCockpit } from './cockpitDestinos';
import { MENSAGEM_AUTOR_NAO_APROVA, souAutorDaConciliacao } from './segregacaoFuncoes';
import api from '../../services/Api';

interface EvidenciaItem {
  evidenciaId: number;
  nomeArquivo: string;
  urlStorage: string;
  tamanhoBytes: number;
}

const money = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

// Extrai "MM/yyyy" direto da string ISO (sem passar por `new Date(...)`), que casa com o
// formato gravado em wf_conciliacoes.periodo_referencia (esteira.PeriodoInicio.ToString("MM/yyyy")
// no backend). Usar `new Date(iso).getMonth()/getFullYear()` aqui quebra: uma data-only ISO
// ("2019-01-01" ou com sufixo "Z") é interpretada como UTC pelo JS, e em fusos negativos
// (Brasil, UTC-3) isso volta um dia — virando dezembro/2018 em vez de janeiro/2019.
const periodoValueFromIso = (iso: string) => `${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

const statusLabel: Record<WorkflowStatus, string> = {
  RASCUNHO: 'Rascunho',
  PENDENTE: 'Pendente',
  EM_PREENCHIMENTO: 'Em preenchimento',
  AGUARDANDO_APROVACAO: 'Aguardando aprovação',
  EM_ANALISE: 'Em análise',
  REJEITADA: 'Rejeitada',
  APROVADA: 'Aprovada',
  BLOQUEADA: 'Bloqueada',
  TRANSBORDADA: 'Transbordada',
  CONCILIADO_AUTOMATICAMENTE: 'Conciliado Automaticamente',
  DIVERGENCIA_DETECTADA: 'Divergência Detectada',
  EM_CONFERENCIA: 'Aguardando complemento',
  OBSOLETA: 'Obsoleta',
};

const riskLabel: Record<RiskLevel, string> = { BAIXO: 'Baixo', MEDIO: 'Médio', ALTO: 'Alto' };

const fechamentoStatusLabel: Record<FechamentoEventoStatus, string> = {
  CONCLUIDO: 'Concluído',
  EM_ANDAMENTO: 'Em andamento',
  PENDENTE: 'Pendente',
  ATRASADO: 'Atrasado',
};

const StatusBadge: React.FC<{ status: WorkflowStatus }> = ({ status }) => (
  <span className={`wf-badge wf-badge--${status.toLowerCase()}`}>{statusLabel[status]}</span>
);

const RiskBadge: React.FC<{ risk: RiskLevel }> = ({ risk }) => (
  <span className={`wf-risk wf-risk--${risk.toLowerCase()}`}>{riskLabel[risk]}</span>
);

const Header: React.FC<{ title: string; subtitle: string; actions?: React.ReactNode }> = ({ title, subtitle, actions }) => (
  <div className='wf-header'>
    <div><h1>{title}</h1><p>{subtitle}</p></div>
    {actions && <div className='wf-header__actions'>{actions}</div>}
  </div>
);

const Card: React.FC<{ title?: string; subtitle?: string; children: React.ReactNode; actions?: React.ReactNode }> = ({ title, subtitle, children, actions }) => (
  <section className='wf-card'>
    {(title || actions) && (
      <div className='wf-card__header'>
        <div>{title && <h2>{title}</h2>}{subtitle && <p>{subtitle}</p>}</div>
        {actions}
      </div>
    )}
    {children}
  </section>
);

interface MetricsProps {
  conciliacoes?: WorkflowConciliacao[];
  vermelho?: number;
  lancamentosCount?: number;
  isAdmin?: boolean;
  /** Quando informado, os cartões viram atalhos para a tela com os itens que compõem o número. */
  contexto?: ContextoCockpit;
  /** Todas as conciliações e as vencidas: permitem ir direto ao item afetado mesmo sem empresa/período selecionados. */
  dadosCockpit?: DadosCockpit;
}

const Metrics: React.FC<MetricsProps> = ({ conciliacoes = [], vermelho = 0, lancamentosCount = 0, isAdmin = false, contexto, dadosCockpit }) => {
  const history = useHistory();
  const aguardando = conciliacoes.filter(c => c.status === 'AGUARDANDO_APROVACAO').length;
  const aguardandoComplemento = conciliacoes.filter(c => c.status === 'EM_CONFERENCIA').length;
  const rejeitadas = conciliacoes.filter(c => c.status === 'REJEITADA').length;
  const aprovadas  = conciliacoes.filter(c => c.status === 'APROVADA').length;
  const riscoAlto  = conciliacoes.filter(c => c.risco === 'ALTO').length;
  const valorPendente = conciliacoes
    .filter(c => ['AGUARDANDO_APROVACAO', 'EM_ANALISE'].includes(c.status))
    .reduce((s, c) => s + (c.valorTotal || 0), 0);
  const dentroSla = conciliacoes.length > 0
    ? Math.round(((conciliacoes.length - vermelho) / conciliacoes.length) * 100) : 0;

  const data: [string, string, string, string, IndicadorCockpit][] = [
    // Administrador TI enxerga todas as contas da empresa, não só as suas — o rótulo não
    // pode dizer "minha aprovação" pra quem está com visão de auditoria/gestão de todo mundo.
    [isAdmin ? 'Contas aguardando aprovação' : 'Aguardando minha aprovação', String(aguardando), `${money(valorPendente)} em análise`, 'primary', 'AGUARDANDO_APROVACAO'],
    ['Em atraso no SLA', String(vermelho), `${vermelho} vencida${vermelho !== 1 ? 's' : ''} há mais de 24h`, 'danger', 'EM_ATRASO_SLA'],
    ['Rejeitadas para correção', String(rejeitadas), `${riscoAlto} com risco alto`, 'warning', 'REJEITADAS'],
    ['Aguardando complemento', String(aguardandoComplemento), 'devolvidas via Solicitar Complemento (SOX)', 'warning', 'AGUARDANDO_COMPLEMENTO'],
    ['Aprovadas no período', String(aprovadas), `${dentroSla}% dentro do SLA`, 'success', 'APROVADAS'],
    ['Lançamentos manuais', String(lancamentosCount), lancamentosCount > 0 ? 'registros no período' : 'nenhum no período', 'info', 'LANCAMENTOS_MANUAIS'],
    ['Risco alto', String(riscoAlto), 'Atuação prioritária', 'dark', 'RISCO_ALTO'],
  ];

  return <div className='wf-metrics'>{data.map(([title, value, detail, color, indicador]) => {
    const conteudo = <><span>{title}</span><strong>{value}</strong><small>{detail}</small></>;
    if (!contexto) return <div className={`wf-metric wf-metric--${color}`} key={title}>{conteudo}</div>;
    const ir = () => { const d = resolverDestinoCockpit(indicador, contexto, dadosCockpit ?? { conciliacoes: [], vencidasIds: [] }); history.push(d.path, d.state); };
    return (
      <div
        className={`wf-metric wf-metric--${color} wf-metric--clicavel`}
        key={title}
        role='button'
        tabIndex={0}
        title={`Ver detalhes: ${title}`}
        onClick={ir}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ir(); } }}
      >
        {conteudo}
      </div>
    );
  })}</div>;
};

interface FiltrosDashboard {
  empresa: string;
  periodo: string;
  responsavel: string;
  status: string;
}

interface PeriodoOpt { value: string; label: string; }

interface FilterBarProps {
  empresas: EmpresaTenant[];
  periodos: PeriodoOpt[];
  responsaveis: string[];
  filtros: FiltrosDashboard;
  onChange: (key: keyof FiltrosDashboard, value: string) => void;
}

const empresaSelectStyles = {
  control: (base: any) => ({ ...base, minHeight: 42, borderRadius: 8, borderColor: '#dfe3ea' }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

const FilterBar: React.FC<FilterBarProps> = ({ empresas, periodos, responsaveis, filtros, onChange }) => {
  const empresaOptions = useMemo(
    () => [...empresas.map(e => ({ value: String(e.id), label: e.nome })), { value: 'TODAS', label: 'Todas as empresas' }],
    [empresas]
  );
  return (
  <div className='wf-filter'>
    <label>Empresa
      <Select
        options={empresaOptions}
        value={empresaOptions.find(opt => opt.value === filtros.empresa) || null}
        onChange={(selected: any) => onChange('empresa', selected?.value || '')}
        placeholder='— Selecione uma empresa —'
        isClearable
        menuPlacement='bottom'
        menuPosition='fixed'
        menuPortalTarget={document.body}
        styles={empresaSelectStyles}
        noOptionsMessage={() => 'Nenhuma empresa encontrada'}
      />
    </label>
    <label>Período
      <select value={filtros.periodo} onChange={e => onChange('periodo', e.target.value)} disabled={!filtros.empresa}>
        <option value='TODOS'>Todos os períodos</option>
        {periodos.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>
    </label>
    <label>Responsável
      <select value={filtros.responsavel} onChange={e => onChange('responsavel', e.target.value)} disabled={!filtros.empresa}>
        <option value='TODOS'>Todos</option>
        {responsaveis.map(r => <option key={r} value={r}>{r}</option>)}
      </select>
    </label>
    <label>Status
      <select value={filtros.status} onChange={e => onChange('status', e.target.value)} disabled={!filtros.empresa}>
        <option value='TODOS'>Todos</option>
        <option value='AGUARDANDO_APROVACAO'>Aguardando aprovação</option>
        <option value='REJEITADA'>Rejeitada</option>
        <option value='APROVADA'>Aprovada</option>
        <option value='RASCUNHO'>Rascunho</option>
      </select>
    </label>
    {(filtros.empresa !== '' || filtros.periodo !== 'TODOS' || filtros.responsavel !== 'TODOS' || filtros.status !== 'TODOS') && (
      <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => { onChange('empresa', ''); onChange('periodo', 'TODOS'); onChange('responsavel', 'TODOS'); onChange('status', 'TODOS'); }}>
        ✕ Limpar filtros
      </button>
    )}
  </div>
  );
};

export const DashboardPage: React.FC = () => {
  const history = useHistory();
  const [navAberta, setNavAberta] = useState(true);
  const [conciliacoes, setConciliacoes] = useState<WorkflowConciliacao[]>([]);
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);
  const [periodos, setPeriodos] = useState<PeriodoOpt[]>([]);
  const [monitoramento, setMonitoramento] = useState<{ itens: MonitoramentoItem[]; verde: number; amarelo: number; vermelho: number }>({ itens: [], verde: 0, amarelo: 0, vermelho: 0 });
  const [cargos, setCargos] = useState<{ cargoId: number; nomeCargo: string; nivelHierarquico: number }[]>([]);
  const [lancamentosCount, setLancamentosCount] = useState(0);
  // Aprovações registradas por nível (nível → quantidade), da empresa/período selecionados
  const [aprovacoesPorNivel, setAprovacoesPorNivel] = useState<Record<number, number>>({});
  const [filtros, setFiltros] = useState<FiltrosDashboard>({ empresa: '', periodo: 'TODOS', responsavel: 'TODOS', status: 'TODOS' });
  // Administrador TI — permissão de configuração do módulo, independente do nível de
  // aprovação N1-N7 (ver CadastroUsuariosPage).
  const [meuAdminTi, setMeuAdminTi] = useState<boolean>(false);

  const filtroChange = (key: keyof FiltrosDashboard, value: string) =>
    setFiltros(prev => ({ ...prev, [key]: value }));

  useEffect(() => {
    workflowService.listarTenants()
      .then(lista => setEmpresas(lista.filter(e => e.grupoId != null && e.status === 'ATIVA')))
      .catch(() => {});
    workflowService.listarMonitoramento().then(setMonitoramento).catch(() => {});
    workflowService.listarCargos().then(setCargos).catch(() => {});
    workflowService.listarLancamentos().then(items => setLancamentosCount(items.length)).catch(() => {});
    workflowService.getMeuPerfil().then(p => setMeuAdminTi(!!p?.adminTi)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!filtros.empresa) {
      setConciliacoes([]);
      setPeriodos([]);
      return;
    }
    workflowService.listarConciliacoes().then(setConciliacoes).catch(() => {});
    if (filtros.empresa !== 'TODAS') {
      const emp = empresas.find(e => String(e.id) === filtros.empresa);
      if (emp?.empresaId) {
        workflowService.getPeriodosEmpresa(emp.empresaId)
          .then(ps => setPeriodos(ps.map(p => ({ value: periodoValueFromIso(p.periodoInicio), label: p.label }))))
          .catch(() => setPeriodos([]));
      } else {
        setPeriodos([]);
      }
    } else {
      setPeriodos([]);
    }
  }, [filtros.empresa, empresas]);

  // Aprovações por nível: refeitas ao trocar empresa ou período, então refletem o que já foi
  // registrado no log SOX. A guarda `ativo` descarta respostas de uma seleção anterior.
  useEffect(() => {
    if (!filtros.empresa) {
      setAprovacoesPorNivel({});
      return;
    }
    let ativo = true;
    workflowService
      .aprovacoesPorNivel(
        filtros.empresa === 'TODAS' ? undefined : Number(filtros.empresa),
        filtros.periodo === 'TODOS' ? undefined : filtros.periodo)
      .then(r => { if (ativo) setAprovacoesPorNivel(aprovacoesPorNivelDaApi(r.niveis)); })
      .catch(() => { if (ativo) setAprovacoesPorNivel({}); });
    return () => { ativo = false; };
  }, [filtros.empresa, filtros.periodo]);

  const responsaveis = useMemo(() => {
    const set = new Set<string>();
    conciliacoes.forEach(c => { if (c.responsavelAtual) set.add(c.responsavelAtual); });
    return Array.from(set).sort();
  }, [conciliacoes]);

  const filteredConciliacoes = useMemo(() => {
    if (!filtros.empresa) return [];
    return conciliacoes.filter(c => {
      if (filtros.empresa !== 'TODAS' && c.empresaId !== Number(filtros.empresa)) return false;
      if (filtros.periodo !== 'TODOS' && c.periodo !== filtros.periodo) return false;
      if (filtros.responsavel !== 'TODOS' && c.responsavelAtual !== filtros.responsavel) return false;
      if (filtros.status !== 'TODOS' && c.status !== filtros.status) return false;
      return true;
    });
  }, [conciliacoes, filtros]);

  const filteredMonitoramento = useMemo(() => {
    if (filtros.empresa === 'TODAS') return monitoramento.itens;
    const empresaNome = empresas.find(e => String(e.id) === filtros.empresa)?.nome;
    return empresaNome ? monitoramento.itens.filter(i => i.empresa === empresaNome) : monitoramento.itens;
  }, [monitoramento.itens, filtros.empresa, empresas]);

  // Barra = conciliações pendentes agrupadas pelo nível hierárquico N1-N7 de quem está com a
  // pendência agora (cargo do grupo responsável ou, se devolvida a um indivíduo, o cargo dele) — não
  // pelo passo 1-6 do processo (nivelAtual), escala diferente dos cargos do funil. Selo = aprovações
  // já registradas por cada nível. Ver funilWorkflow.ts.
  const stageSummary = useMemo(
    () => montarFunil(filteredConciliacoes, cargos, aprovacoesPorNivel),
    [filteredConciliacoes, cargos, aprovacoesPorNivel]);

  const max = Math.max(...stageSummary.map(s => s.total), 1);

  const proximosPrazos = useMemo<FechamentoEvento[]>(
    () => eventosDePrazos(filteredMonitoramento).sort((a, b) => a.data.localeCompare(b.data)).slice(0, 4),
    [filteredMonitoramento]);

  const filteredVermelho = filteredMonitoramento.filter(i => i.slaStatus === 'VERMELHO').length;
  const totalPendente = filteredConciliacoes.filter(c => !['APROVADA', 'CANCELADA'].includes(c.status));
  const valorAprovacao = totalPendente.reduce((s, c) => s + (c.valorTotal || 0), 0);
  const dentroSla = filteredConciliacoes.length > 0 ? Math.round(((filteredConciliacoes.length - filteredVermelho) / filteredConciliacoes.length) * 100) : 0;
  const transbordadas = filteredConciliacoes.filter(c => c.status === 'TRANSBORDADA').length;
  const saude = filteredConciliacoes.length > 0 ? Math.round((filteredConciliacoes.filter(c => c.status === 'APROVADA').length / filteredConciliacoes.length) * 100) : 0;
  const filtroAtivo = filtros.empresa !== '' && (filtros.periodo !== 'TODOS' || filtros.responsavel !== 'TODOS' || filtros.status !== 'TODOS');

  return <div className='wf-page'>
    <Header title='Workflow SOX' subtitle='Acompanhe aprovações, SLA, riscos e pendências do fechamento contábil.' actions={<>
      <button className='wf-btn wf-btn--secondary' onClick={() => history.push('/workflow-aprovacao/calendario-fechamento')}>Calendário de fechamento</button>
      <button className='wf-btn wf-btn--primary' onClick={() => history.push('/workflow-aprovacao/conciliacoes')}>Ver conciliações</button>
    </>} />
    <FilterBar empresas={empresas} periodos={periodos} responsaveis={responsaveis} filtros={filtros} onChange={filtroChange} />
    {!filtros.empresa && <p className='wf-filter-info' style={{ color: '#6c757d' }}>Selecione uma empresa para visualizar as conciliações.</p>}
    {filtros.empresa && filtroAtivo && <p className='wf-filter-info'>Exibindo {filteredConciliacoes.length} de {conciliacoes.length} conciliações com os filtros aplicados.</p>}
    <Card title='Navegação rápida' subtitle={navAberta ? 'Selecione uma empresa ou "Todas" para acessar qualquer módulo.' : 'Clique em expandir para ver todos os módulos.'} actions={
      <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => setNavAberta(v => !v)} style={{ minWidth: 100 }}>
        {navAberta ? '▲ Minimizar' : '▼ Expandir'}
      </button>
    }>
      {navAberta && <div className='wf-nav-grid'>
        {[
          { label: 'Conciliações',         icon: '📋', path: '/workflow-aprovacao/conciliacoes',          desc: 'Lista e detalhe de conciliações',       color: 'primary', admin: false },
          { label: 'Monitoramento / SLA',  icon: '🔴', path: '/workflow-aprovacao/monitoramento',          desc: 'Semáforos e alertas de prazo',           color: 'danger',  admin: false },
          { label: 'Calendário',           icon: '📅', path: '/workflow-aprovacao/calendario-fechamento',  desc: 'Prazos do ciclo contábil',               color: 'info',    admin: false },
          { label: 'Lançamentos Manuais',  icon: '✏️', path: '/workflow-aprovacao/lancamentos-manuais',   desc: 'Governança de entradas manuais',         color: 'warning', admin: false },
          { label: 'Pendências Transv.',   icon: '⏳', path: '/workflow-aprovacao/pendencias-transversais',desc: 'Transbordo entre ciclos',                color: 'warning', admin: false },
          { label: 'Auditoria SOX',        icon: '🔒', path: '/workflow-aprovacao/auditoria-sox',          desc: 'Log imutável de execução',               color: 'dark',    admin: true },
          { label: 'Cad. Usuários',        icon: '👤', path: '/workflow-aprovacao/cadastro-usuarios',      desc: 'Gerencie colaboradores N1–N7',           color: 'success', admin: true },
          { label: 'Cad. Cargos',          icon: '🏷️', path: '/workflow-aprovacao/cadastro-cargos',       desc: 'Níveis hierárquicos e permissões',       color: 'success', admin: true },
          { label: 'Cad. Grupos',          icon: '🏛️', path: '/workflow-aprovacao/cadastro-grupos',       desc: 'Grupos econômicos e vínculos',           color: 'success', admin: true },
          { label: 'Vínculos & SOX',       icon: '🔗', path: '/workflow-aprovacao/grupos-economicos',      desc: 'Rastreabilidade de estrutura',           color: 'dark',    admin: true },
          { label: 'Matriz de Acessos',    icon: '🔑', path: '/workflow-aprovacao/matriz-acessos',         desc: 'Permissões usuário × empresa',           color: 'dark',    admin: true },
          { label: 'Config. Alçadas',      icon: '⚖️', path: '/workflow-aprovacao/config-alcadas',        desc: 'Wizard de escopo, aprovação e SLA em dias úteis', color: 'dark', admin: true },
          { label: 'Governança de TI',     icon: '🛡️', path: '/workflow-aprovacao/governanca-ti',         desc: 'Travas e políticas do Administrador TI', color: 'dark',    admin: true },
          { label: 'Configurações',        icon: '⚙️', path: '/workflow-aprovacao/configuracoes',         desc: 'Alçadas e etapas do fluxo',              color: 'dark',    admin: true },
          { label: 'Onboarding SOX',       icon: '🚀', path: '/workflow-aprovacao/onboarding',              desc: 'Wizard de configuração — 5 etapas',    color: 'primary', admin: true },
          { label: 'De/Para Contas',       icon: '🔀', path: '/workflow-aprovacao/cadastro-depara',           desc: 'Mapeamento ECD → grupos de negócio', color: 'info',    admin: true },
          { label: 'Catálogo PDCA',        icon: '📝', path: '/workflow-aprovacao/cadastro-justificativas',   desc: 'Justificativas e planos de melhoria pré-cadastrados', color: 'success', admin: true },
          { label: 'Matriz Responsab.',    icon: '🗂️', path: '/workflow-aprovacao/matriz-responsabilidades', desc: 'Contas × Aprovadores SOX (SoD)',      color: 'dark',    admin: true },
          { label: 'Integridade Cad.',     icon: '📊', path: '/workflow-aprovacao/dashboard-integridade',    desc: 'Cobertura SOX e pendências de vínculo', color: 'warning', admin: true },
        ].filter(item => !item.admin || meuAdminTi)
          .map(item => <button key={item.path} className={`wf-nav-card wf-nav-card--${item.color}`} onClick={() => history.push(item.path)}>
          <span className='wf-nav-card__icon'>{item.icon}</span>
          <strong>{item.label}</strong>
          <small>{item.desc}</small>
        </button>)}
      </div>}
    </Card>
    <Metrics conciliacoes={filteredConciliacoes} vermelho={filteredVermelho} lancamentosCount={lancamentosCount} isAdmin={meuAdminTi} contexto={{ empresaId: filtros.empresa, periodo: filtros.periodo }} dadosCockpit={{ conciliacoes: conciliacoes.map(c => ({ id: c.id, empresaId: c.empresaId, periodo: c.periodo, status: c.status, risco: c.risco })), vencidasIds: monitoramento.itens.filter(i => i.slaStatus === 'VERMELHO').map(i => i.conciliacaoId) }} />
    <div className='wf-grid-2'>
      <Card title='Funil do workflow' subtitle='Barra: conciliações pendentes por nível. Selo verde: aprovações registradas por nível (empresa e período selecionados).'>
        <div className='wf-funnel'>{stageSummary.map(s => <div className='wf-funnel__row' key={s.nivel} data-testid={`funil-n${s.nivel}`}>
          <strong>N{s.nivel}</strong>
          <span className='wf-funnel__label'>
            {s.cargo}
            <small className='wf-funnel__aprovacoes' title={`Aprovações registradas por usuários do nível N${s.nivel}`}>
              {rotuloAprovacoes(s.aprovacoes)}
            </small>
          </span>
          <div className='wf-funnel__track'>
            <div style={{ width: `${Math.max((s.total / max) * 100, 10)}%` }}>
              {s.total}
            </div>
          </div>
        </div>)}</div>
      </Card>
      <Card title='Indicadores operacionais' subtitle='Desempenho geral do fluxo.'>
        <div className='wf-kpis'>
          <div><span>Tempo médio</span><strong data-testid='tempo-medio'>{formatarDuracao(tempoMedioAprovacaoHoras(filteredConciliacoes))}</strong></div>
          <div><span>Dentro do SLA</span><strong>{dentroSla}%</strong></div>
          <div><span>Valor em aprovação</span><strong>{money(valorAprovacao)}</strong></div>
          <div><span>Transbordadas</span><strong>{transbordadas}</strong></div>
        </div>
        <div className='wf-health'><div><span>Saúde do fechamento</span><strong>{saude}%</strong></div><div className='wf-health__track'><div style={{ width: `${saude}%` }} /></div><small>{100 - saude}% depende de correção, evidência ou aprovação.</small></div>
      </Card>
    </div>
    <Card title='Conciliações prioritárias' subtitle={filtroAtivo ? `${filteredConciliacoes.length} resultado(s) com filtros aplicados.` : 'Itens que exigem acompanhamento.'} actions={<button className='wf-link' onClick={() => history.push('/workflow-aprovacao/conciliacoes')}>Ver todas</button>}>
      <ConciliationTable
        items={filteredConciliacoes.slice(0, 5)}
        emptyMsg={!filtros.empresa
          ? 'Selecione uma empresa no filtro acima para visualizar as conciliações prioritárias.'
          : 'Nenhuma conciliação prioritária no momento.'}
      />
    </Card>
    <Card title='Calendário de fechamento' subtitle='Próximos prazos do ciclo contábil.' actions={<button className='wf-link' onClick={() => history.push('/workflow-aprovacao/calendario-fechamento')}>Ver calendário completo</button>}>
      {proximosPrazos.length === 0
        ? <p style={{ padding: '20px 0', color: '#a1a5b7', fontSize: 13, textAlign: 'center' }}>
            Nenhum prazo configurado. Os prazos aparecerão aqui após configurar as alçadas com SLA e enviar conciliações para aprovação.
          </p>
        : <div className='wf-cal-events'>{proximosPrazos.map(evento => <div className='wf-cal-event wf-cal-event--compact' key={evento.id}>
            <div className='wf-cal-event__top'><strong>{evento.data.split('-').reverse().join('/')} — {evento.titulo}</strong><span className={`wf-cal-badge wf-cal-badge--${evento.status.toLowerCase()}`}>{fechamentoStatusLabel[evento.status]}</span></div>
            <span className='wf-cal-event__meta'>{evento.empresa}</span>
          </div>)}</div>
      }
    </Card>

  </div>;
};

const ConciliationTable: React.FC<{ items: WorkflowConciliacao[]; emptyMsg?: string }> = ({ items, emptyMsg }) => {
  const history = useHistory();
  // id da conciliação cuja capa está sendo gerada para impressão (evita cliques repetidos)
  const [imprimindoId, setImprimindoId] = useState<number | null>(null);
  const imprimirCapa = async (id: number) => {
    setImprimindoId(id);
    try { await abrirCapaParaImpressao(id); } finally { setImprimindoId(null); }
  };
  if (items.length === 0) {
    return (
      <p style={{ padding: '20px 0', color: '#a1a5b7', fontSize: 13, textAlign: 'center' }}>
        {emptyMsg ?? 'Nenhuma conciliação encontrada.'}
      </p>
    );
  }
  return <div className='wf-table-wrap'><table className='wf-table'><thead><tr><th>Empresa / Conta</th><th>Valor</th><th>Etapa</th><th>Responsável</th><th>SLA</th><th>Risco</th><th>Status</th><th /></tr></thead><tbody>
    {items.map(item => <tr key={item.id}>
      <td><strong>{item.empresa}</strong><span>{item.conta} — {item.descricaoConta}</span></td>
      <td>{money(item.valorTotal)}</td>
      <td>{item.nomeEtapa || `Etapa ${item.etapaAtual}`}<span>Passo {item.nivelAtual} de 6</span></td>
      <td>{item.responsavelAtual}</td>
      <td className={item.sla.toLowerCase().includes('vencido') ? 'wf-danger-text' : ''}>{item.sla}</td>
      <td><RiskBadge risk={item.risco} /></td>
      <td><StatusBadge status={item.status} /></td>
      <td className='wf-table-actions'>
        <button className='wf-btn wf-btn--primary wf-btn--sm' onClick={() => history.push(`/workflow-aprovacao/conciliacoes/${item.id}`)}>Abrir</button>
        <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => item.cartaoId ? window.open(`/capa-conciliacao/${item.cartaoId}`, '_blank') : history.push(`/workflow-aprovacao/conciliacoes/${item.id}/capa`)}>Ver Capa</button>
        <button
          className='wf-btn wf-btn--secondary wf-btn--sm'
          disabled={imprimindoId === item.id}
          title='Abre somente a capa SOX desta conciliação em outra aba, para imprimir ou salvar em PDF'
          onClick={() => imprimirCapa(item.id)}
        >
          {imprimindoId === item.id ? 'Gerando…' : '🖨 Imprimir capa'}
        </button>
      </td>
    </tr>)}
  </tbody></table></div>;
};

export const ConciliacoesPage: React.FC = () => {
  const history = useHistory();
  // Ao voltar de uma ação (aprovar/devolver) na tela de detalhe, chega aqui com a empresa e
  // o período que já estavam selecionados, pra não precisar escolher tudo de novo.
  const location = useLocation<{ empresaId?: string; periodo?: string; status?: string; risco?: string } | undefined>();
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);
  const [empresaId, setEmpresaId] = useState(location.state?.empresaId ?? '');
  const [periodos, setPeriodos] = useState<PeriodoOpt[]>([]);
  const [periodo, setPeriodo] = useState(location.state?.periodo ?? '');
  const [conciliacoes, setConciliacoes] = useState<WorkflowConciliacao[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'TODOS' | WorkflowStatus>((location.state?.status as WorkflowStatus | undefined) ?? 'TODOS');
  const [risk, setRisk] = useState<'TODOS' | RiskLevel>((location.state?.risco as RiskLevel | undefined) ?? 'TODOS');

  useEffect(() => {
    workflowService.listarTenants()
      .then(lista => setEmpresas(lista.filter(e => e.grupoId != null && e.status === 'ATIVA')))
      .catch(() => setEmpresas([]));
  }, []);

  useEffect(() => {
    if (!empresaId) { setConciliacoes([]); setPeriodo(''); setPeriodos([]); return; }
    setLoading(true);
    workflowService.listarConciliacoes().then(setConciliacoes).catch(() => setConciliacoes([])).finally(() => setLoading(false));

    // Mesmo seletor de período do Mapa de Conciliação/dashboard — por esteira, não pelos
    // dados da própria conciliação (evita listas de período divergentes entre telas).
    const emp = empresas.find(e => String(e.id) === empresaId);
    if (emp?.empresaId) {
      workflowService.getPeriodosEmpresa(emp.empresaId)
        .then(ps => setPeriodos(ps.map(p => ({ value: periodoValueFromIso(p.periodoInicio), label: p.label }))))
        .catch(() => setPeriodos([]));
    } else {
      setPeriodos([]);
    }
  }, [empresaId, empresas]);

  const conciliacoesDaEmpresa = useMemo(
    () => conciliacoes.filter(c => String(c.empresaId) === empresaId),
    [conciliacoes, empresaId]
  );

  const filtered = useMemo(() => {
    if (!empresaId || !periodo) return [];
    const q = search.toLowerCase().trim();
    return conciliacoesDaEmpresa.filter(item => {
      const textOk = !q || `${item.empresa} ${item.conta} ${item.descricaoConta}`.toLowerCase().includes(q);
      return item.periodo === periodo
        && textOk
        && (status === 'TODOS' || item.status === status)
        && (risk === 'TODOS' || item.risco === risk);
    });
  }, [conciliacoesDaEmpresa, empresaId, periodo, search, status, risk]);

  const isReady = !!empresaId && !!periodo;

  return <div className='wf-page'>
    <Header title='Conciliações' subtitle='Consulte estágio, responsável, SLA e risco de cada conciliação.' actions={<button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>} />
    <Card>
      <div className='wf-search'>
        <select value={empresaId} onChange={e => { setEmpresaId(e.target.value); setPeriodo(''); }}>
          <option value=''>— Selecione a empresa —</option>
          {empresas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        <select value={periodo} onChange={e => setPeriodo(e.target.value)} disabled={!empresaId || periodos.length === 0}>
          <option value=''>
            {!empresaId ? 'Selecione a empresa primeiro' : periodos.length === 0 ? 'Sem períodos disponíveis' : '— Selecione o período —'}
          </option>
          {periodos.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>
      {isReady && (
        <div className='wf-search' style={{ marginTop: 10 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder='Pesquisar empresa, conta ou descrição...' />
          <select value={status} onChange={e => setStatus(e.target.value as 'TODOS' | WorkflowStatus)}><option value='TODOS'>Todos os status</option><option value='AGUARDANDO_APROVACAO'>Aguardando aprovação</option><option value='EM_ANALISE'>Em análise</option><option value='REJEITADA'>Rejeitada</option><option value='APROVADA'>Aprovada</option><option value='BLOQUEADA'>Bloqueada</option></select>
          <select value={risk} onChange={e => setRisk(e.target.value as 'TODOS' | RiskLevel)}><option value='TODOS'>Todos os riscos</option><option value='BAIXO'>Baixo</option><option value='MEDIO'>Médio</option><option value='ALTO'>Alto</option></select>
          <button className='wf-btn wf-btn--secondary' onClick={() => { setSearch(''); setStatus('TODOS'); setRisk('TODOS'); }}>Limpar</button>
        </div>
      )}
      {!isReady ? (
        <p className='wf-count' style={{ textAlign: 'center', padding: 24 }}>
          {!empresaId ? 'Selecione uma empresa para consultar as conciliações.' : 'Selecione o período.'}
        </p>
      ) : (
        <>
          <p className='wf-count'>{loading ? 'Carregando...' : `${filtered.length} conciliação(ões) encontrada(s)`}</p>
          <ConciliationTable items={filtered} />
        </>
      )}
    </Card>
  </div>;
};

interface DetailParams { id: string; }
const FASES_PDCA: { key: 'plan' | 'do' | 'check' | 'act'; label: string }[] = [
  { key: 'plan', label: 'PLAN' }, { key: 'do', label: 'DO' }, { key: 'check', label: 'CHECK' }, { key: 'act', label: 'ACT' },
];
const PILARES_PDCA: { key: 'pessoas' | 'processos' | 'tecnologia'; label: string; icon: string }[] = [
  { key: 'pessoas', label: 'Pessoas', icon: '👤' }, { key: 'processos', label: 'Processos', icon: '⚙️' }, { key: 'tecnologia', label: 'Tecnologia', icon: '💻' },
];
const planoStatusLabel: Record<string, string> = {
  NAO_INICIADO: 'Não iniciado', EM_ANDAMENTO: 'Em andamento', AGUARDANDO_VALIDACAO: 'Aguardando validação', CONCLUIDO: 'Concluído', VALIDADO: 'Validado',
};

const PlanoMelhoriaSection: React.FC<{ concilId: number }> = ({ concilId }) => {
  const [plano, setPlano] = useState<PlanoMelhoria | null>(null);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<JustificativaTemplate[]>([]);
  const [novoTemplateId, setNovoTemplateId] = useState<number | ''>('');
  const [novaJustificativa, setNovaJustificativa] = useState('');
  const [criando, setCriando] = useState(false);
  const [checkObs, setCheckObs] = useState('');
  const [atoTexto, setAtoTexto] = useState('');
  const [salvandoCheck, setSalvandoCheck] = useState(false);
  const [salvandoAto, setSalvandoAto] = useState(false);
  const [encerrando, setEncerrando] = useState(false);
  const [novaAcao, setNovaAcao] = useState<PlanoMelhoriaAcaoInput>({ descricao: '', status: 'NAO_INICIADO', pilar: 'PROCESSOS', fase: 'PLAN' });
  const [addingAcao, setAddingAcao] = useState(false);
  const [erro, setErro] = useState('');

  // PDCA fica editável (seção 17 do prompt SOX): conteúdo pré-preenchido pelo template,
  // mas o usuário autorizado pode complementar cada campo antes de salvar.
  const [pdcaEdit, setPdcaEdit] = useState<PdcaConteudo | null>(null);
  const [salvandoPdca, setSalvandoPdca] = useState(false);

  useEffect(() => {
    setLoading(true);
    workflowService.obterPlanoMelhoria(concilId).then(p => { setPlano(p); setPdcaEdit(p?.pdca ?? null); }).catch(() => setPlano(null)).finally(() => setLoading(false));
    workflowService.listarJustificativaTemplates().then(setTemplates).catch(() => {});
  }, [concilId]);

  const alterarCampoPdca = (fase: 'plan' | 'do' | 'check' | 'act', pilar: 'pessoas' | 'processos' | 'tecnologia', valor: string) => {
    setPdcaEdit(prev => prev && { ...prev, [fase]: { ...prev[fase], [pilar]: valor } });
  };

  const pdcaAlterado = plano && pdcaEdit ? JSON.stringify(pdcaEdit) !== JSON.stringify(plano.pdca) : false;

  const salvarPdca = async () => {
    if (!plano || !pdcaEdit) return;
    setSalvandoPdca(true);
    try {
      setPlano(await workflowService.atualizarPlanoMelhoria(plano.planoId, { pdcaJson: JSON.stringify(pdcaEdit) }));
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Erro ao salvar alterações do PDCA.'));
    } finally {
      setSalvandoPdca(false);
    }
  };

  const handleSelecionarTemplateNovo = (id: number) => {
    setNovoTemplateId(id);
    const t = templates.find(x => x.templateId === id);
    setNovaJustificativa(t && t.templateId !== 10 ? t.justificativaDetalhada : '');
  };

  const criar = async () => {
    if (!novoTemplateId || !novaJustificativa.trim()) return;
    setCriando(true);
    setErro('');
    try {
      const t = templates.find(x => x.templateId === novoTemplateId);
      const p = await workflowService.criarPlanoMelhoria({
        concilId, templateId: Number(novoTemplateId),
        justificativaDetalhada: novaJustificativa.trim(),
        pdcaJson: t ? JSON.stringify(t.pdca) : undefined,
      });
      setPlano(p);
    } catch (err: any) {
      setErro(err?.response?.data?.message ?? 'Erro ao criar plano de melhoria.');
    } finally {
      setCriando(false);
    }
  };

  const salvarCheck = async () => {
    if (!plano) return;
    setSalvandoCheck(true);
    try {
      setPlano(await workflowService.registrarCheckPlanoMelhoria(plano.planoId, true, checkObs || undefined));
    } catch (err) { setErro(extrairMensagemErro(err, 'Erro ao registrar CHECK.')); } finally { setSalvandoCheck(false); }
  };

  const salvarAto = async () => {
    if (!plano || !atoTexto.trim()) return;
    setSalvandoAto(true);
    try {
      setPlano(await workflowService.registrarAtoPlanoMelhoria(plano.planoId, atoTexto.trim()));
    } catch (err) { setErro(extrairMensagemErro(err, 'Erro ao registrar ACT.')); } finally { setSalvandoAto(false); }
  };

  const encerrar = async () => {
    if (!plano) return;
    setEncerrando(true);
    setErro('');
    try {
      setPlano(await workflowService.encerrarPlanoMelhoria(plano.planoId));
    } catch (err: any) {
      setErro(extrairMensagemErro(err, 'Erro ao encerrar plano.'));
    } finally { setEncerrando(false); }
  };

  const adicionarAcao = async () => {
    if (!plano || !novaAcao.descricao.trim()) return;
    setAddingAcao(true);
    try {
      await workflowService.adicionarAcaoPlanoMelhoria(plano.planoId, novaAcao);
      setPlano(await workflowService.obterPlanoMelhoria(concilId));
      setNovaAcao({ descricao: '', status: 'NAO_INICIADO', pilar: 'PROCESSOS', fase: 'PLAN' });
    } catch (err) { setErro(extrairMensagemErro(err, 'Erro ao adicionar ação.')); } finally { setAddingAcao(false); }
  };

  const atualizarAcaoStatus = async (acaoId: number, status: PlanoMelhoriaAcaoInput['status'], acaoAtual: PlanoMelhoriaAcaoInput) => {
    try {
      await workflowService.atualizarAcaoPlanoMelhoria(acaoId, { ...acaoAtual, status });
      setPlano(await workflowService.obterPlanoMelhoria(concilId));
    } catch (err) { setErro(extrairMensagemErro(err, 'Erro ao atualizar ação.')); }
  };

  if (loading) return null;

  return (
    <section className='wf-card' style={{ marginTop: 16 }}>
      <div className='wf-card__header'>
        <div><h2>Plano de Melhoria (PDCA)</h2><p>Justificativa e plano de correção conforme metodologia PDCA — Pessoas, Processos e Tecnologia.</p></div>
        {plano && <span className={`wf-badge ${plano.status === 'VALIDADO' ? 'wf-badge--aprovada' : 'wf-badge--em_analise'}`}>{planoStatusLabel[plano.status]}</span>}
      </div>

      {erro && <p className='wf-danger-text' style={{ fontSize: 12 }}>{erro}</p>}

      {!plano ? (
        <div className='wf-form-grid wf-form-grid--one'>
          <label>Justificativa e plano de correção pré-definidos
            <select value={novoTemplateId} onChange={e => handleSelecionarTemplateNovo(Number(e.target.value))}>
              <option value='' disabled>— Selecione uma opção —</option>
              {templates.map(t => <option key={t.templateId} value={t.templateId}>{t.templateId} — {t.titulo}</option>)}
            </select>
          </label>
          <label>Justificativa detalhada
            <textarea rows={3} value={novaJustificativa} onChange={e => setNovaJustificativa(e.target.value)} />
          </label>
          <button className='wf-btn wf-btn--primary wf-btn--sm' style={{ justifySelf: 'start' }} disabled={!novoTemplateId || !novaJustificativa.trim() || criando} onClick={criar}>
            {criando ? 'Criando...' : 'Criar Plano de Melhoria'}
          </button>
        </div>
      ) : (
        <>
          <p style={{ fontSize: 13, color: '#5e6278' }}>{plano.justificativaDetalhada}</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginTop: 12 }}>
            {FASES_PDCA.map(f => (
              <div key={f.key} style={{ border: '1px solid #e8edf3', borderRadius: 10, padding: 12 }}>
                <strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>{f.label}</strong>
                {PILARES_PDCA.map(p => (
                  <label key={p.key} style={{ display: 'block', fontSize: 11, color: '#5e6278', marginBottom: 6 }}>
                    <strong>{p.icon} {p.label}</strong>
                    <textarea
                      rows={3}
                      value={pdcaEdit?.[f.key][p.key] ?? ''}
                      onChange={e => alterarCampoPdca(f.key, p.key, e.target.value)}
                      style={{ width: '100%', marginTop: 4, fontSize: 11, resize: 'vertical' }}
                    />
                  </label>
                ))}
              </div>
            ))}
          </div>
          {pdcaAlterado && (
            <div style={{ marginTop: 10 }}>
              <button className='wf-btn wf-btn--secondary wf-btn--sm' disabled={salvandoPdca} onClick={salvarPdca}>
                {salvandoPdca ? 'Salvando...' : 'Salvar alterações do PDCA'}
              </button>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <strong style={{ fontSize: 13 }}>Ações do plano</strong>
            <div className='wf-table-wrap' style={{ marginTop: 8 }}>
              <table className='wf-table'>
                <thead><tr><th>Fase</th><th>Pilar</th><th>Descrição</th><th>Responsável</th><th>Prazo</th><th>Status</th></tr></thead>
                <tbody>
                  {plano.acoes.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#a1a5b7' }}>Nenhuma ação cadastrada.</td></tr>}
                  {plano.acoes.map(a => (
                    <tr key={a.acaoId}>
                      <td>{a.fase}</td><td>{a.pilar}</td><td>{a.descricao}</td>
                      <td>{a.responsavelNome ?? '—'}</td>
                      <td>{a.prazo ? new Date(a.prazo).toLocaleDateString('pt-BR') : '—'}</td>
                      <td>
                        <select value={a.status} onChange={e => atualizarAcaoStatus(a.acaoId, e.target.value as PlanoMelhoriaAcaoInput['status'], a)}>
                          <option value='NAO_INICIADO'>Não iniciado</option>
                          <option value='EM_ANDAMENTO'>Em andamento</option>
                          <option value='AGUARDANDO_VALIDACAO'>Aguardando validação</option>
                          <option value='CONCLUIDO'>Concluído</option>
                          <option value='VALIDADO'>Validado</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ flex: 2, minWidth: 160 }}>Descrição<input value={novaAcao.descricao} onChange={e => { const value_ = e.target.value; setNovaAcao(a => ({ ...a, descricao: value_ })); }} /></label>
              <label>Fase<select value={novaAcao.fase} onChange={e => { const value_ = e.target.value; setNovaAcao(a => ({ ...a, fase: value_ as PlanoMelhoriaAcaoInput['fase'] })); }}><option value='PLAN'>Plan</option><option value='DO'>Do</option><option value='CHECK'>Check</option><option value='ACT'>Act</option></select></label>
              <label>Pilar<select value={novaAcao.pilar} onChange={e => { const value_ = e.target.value; setNovaAcao(a => ({ ...a, pilar: value_ as PlanoMelhoriaAcaoInput['pilar'] })); }}><option value='PESSOAS'>Pessoas</option><option value='PROCESSOS'>Processos</option><option value='TECNOLOGIA'>Tecnologia</option></select></label>
              <label>Prazo<input type='date' value={novaAcao.prazo ?? ''} onChange={e => { const value_ = e.target.value; setNovaAcao(a => ({ ...a, prazo: value_ })); }} /></label>
              <button className='wf-btn wf-btn--secondary wf-btn--sm' disabled={!novaAcao.descricao.trim() || addingAcao} onClick={adicionarAcao}>+ Ação</button>
            </div>
          </div>

          <div className='wf-form-grid' style={{ marginTop: 16 }}>
            <div style={{ border: '1px solid #e8edf3', borderRadius: 10, padding: 12 }}>
              <strong style={{ fontSize: 13 }}>CHECK — a correção foi eficaz?</strong>
              {plano.checkValidado
                ? <p className='wf-success-text' style={{ fontSize: 12, marginTop: 8 }}>✓ Validado por {plano.checkValidadoPorNome} em {plano.checkValidadoEm ? new Date(plano.checkValidadoEm).toLocaleString('pt-BR') : ''}{plano.checkObservacao ? ` — ${plano.checkObservacao}` : ''}</p>
                : <>
                    <textarea rows={2} placeholder='Observação da validação...' value={checkObs} onChange={e => setCheckObs(e.target.value)} style={{ width: '100%', margin: '8px 0' }} />
                    <button className='wf-btn wf-btn--secondary wf-btn--sm' disabled={salvandoCheck} onClick={salvarCheck}>Validar CHECK</button>
                  </>}
            </div>
            <div style={{ border: '1px solid #e8edf3', borderRadius: 10, padding: 12 }}>
              <strong style={{ fontSize: 13 }}>ACT — medida de prevenção/padronização</strong>
              {plano.atoRegistrado ? (
                <p style={{ fontSize: 12, marginTop: 8 }}>{plano.atoTexto}<br /><span className='wf-success-text'>✓ Registrado por {plano.atoRegistradoPorNome}</span></p>
              ) : (
                <>
                  <textarea rows={2} placeholder='Descreva a medida adotada...' value={atoTexto} onChange={e => setAtoTexto(e.target.value)} style={{ width: '100%', margin: '8px 0' }} />
                  <button className='wf-btn wf-btn--secondary wf-btn--sm' disabled={!atoTexto.trim() || salvandoAto} onClick={salvarAto}>Registrar ACT</button>
                </>
              )}
            </div>
          </div>

          {plano.status !== 'VALIDADO' && (
            <div style={{ marginTop: 14 }}>
              <button className='wf-btn wf-btn--primary wf-btn--sm' disabled={!plano.podeEncerrar || encerrando} onClick={encerrar} title={!plano.podeEncerrar ? 'Exige CHECK validado e ACT registrado' : undefined}>
                {encerrando ? 'Encerrando...' : 'Encerrar Plano de Melhoria'}
              </button>
              {!plano.podeEncerrar && <span style={{ fontSize: 11, color: '#a1a5b7', marginLeft: 8 }}>Exige CHECK validado e ACT registrado.</span>}
            </div>
          )}
        </>
      )}
    </section>
  );
};

const HistoricoSoxSection: React.FC<{ concilId: number }> = ({ concilId }) => {
  const [eventos, setEventos] = useState<SoxEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    workflowService.listarHistoricoConciliacao(concilId)
      .then(lista => setEventos([...lista].reverse())) // mais antigo primeiro — lê como uma linha do tempo
      .catch(() => setEventos([]))
      .finally(() => setLoading(false));
  }, [concilId]);

  return (
    <section className='wf-card' style={{ marginTop: 16 }}>
      <div className='wf-card__header'>
        <div>
          <h2>Log de Rastreio SOX</h2>
          <p>Linha do tempo completa desta conta: da entrada no Workflow até a etapa atual, com quem agiu, quando e por quê.</p>
        </div>
      </div>
      {loading ? (
        <p className='wf-count' style={{ textAlign: 'center', padding: 16 }}>Carregando histórico...</p>
      ) : eventos.length === 0 ? (
        <p className='wf-count' style={{ textAlign: 'center', padding: 16 }}>Nenhum evento de auditoria encontrado.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {eventos.map(ev => (
            <div key={ev.id} style={{ display: 'flex', gap: 12, borderLeft: '3px solid #009ef7', paddingLeft: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13 }}>{ev.acao}</strong>
                  <span style={{ fontSize: 11, color: '#a1a5b7' }}>{ev.dataHora}</span>
                </div>
                <div style={{ fontSize: 12, color: '#5e6278', marginTop: 2 }}>
                  {ev.executor}{ev.etapa && ev.etapa !== '—' ? ` — ${ev.etapa}` : ''}
                </div>
                {ev.justificativa && (
                  <p style={{ fontSize: 12, color: '#7e8299', marginTop: 4, marginBottom: 0 }}>{ev.justificativa}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export const DetalhePage: React.FC = () => {
  const { id } = useParams<DetailParams>();
  const history = useHistory();
  const [item, setItem] = useState<WorkflowConciliacao | undefined>(undefined);
  const [meuUserId, setMeuUserId] = useState<number | null>(null);
  useEffect(() => { workflowService.getMeuPerfil().then(p => setMeuUserId(p?.id ?? null)).catch(() => setMeuUserId(null)); }, []);
  const [loadingDetalhe, setLoadingDetalhe] = useState(true);
  const [activeStep, setActiveStep] = useState(1);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [evidencias, setEvidencias] = useState<EvidenciaItem[]>([]);
  const [showSolicitar, setShowSolicitar] = useState(false);
  const [motivoDevolucao, setMotivoDevolucao] = useState<MotivoDevolucao | ''>('');
  const [observacaoSolicitar, setObservacaoSolicitar] = useState('');
  const [loadingSolicitar, setLoadingSolicitar] = useState(false);
  const [showResolverComplemento, setShowResolverComplemento] = useState(false);
  const [observacaoComplemento, setObservacaoComplemento] = useState('');
  const [resolvendoComplemento, setResolvendoComplemento] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    workflowService.obterConciliacao(Number(id))
      .then(data => {
        if (data) {
          setItem(data);
          setActiveStep(data.etapaAtual);
          // Preencher justificativa técnica se vier do backend
          const raw = data as any;
          if (raw.justificativaTecnica) setJustificativa(raw.justificativaTecnica);
          // Preencher evidências se vierem no response
          if (Array.isArray(raw.evidencias)) setEvidencias(raw.evidencias);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingDetalhe(false));
  }, [id]);

  const salvarJustificativa = async () => {
    if (!item) return;
    try {
      await api.put(`workflow/conciliacoes/${item.id}/justificativa`, { justificativaTecnica: justificativa });
      setMessage('Justificativa salva. Log SOX registrado.');
    } catch (err) {
      setMessage(extrairMensagemErro(err, 'Não foi possível salvar a justificativa.'));
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!item) return;
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      try {
        await workflowService.uploadEvidencia(item.id, file, file.name);
      } catch (err) {
        setMessage(`${file.name}: ${extrairMensagemErro(err, 'não foi possível enviar a evidência.')}`);
      }
    }
    // Recarregar conciliação para atualizar lista de evidências
    workflowService.obterConciliacao(item.id).then(data => {
      if (data) {
        const raw = data as any;
        if (Array.isArray(raw.evidencias)) setEvidencias(raw.evidencias);
      }
    }).catch(() => {});
  };

  const exportar = async (formato: 'pdf' | 'excel') => {
    if (!item) return;
    try {
      const blob = await workflowService.exportarConciliacao(item.id, formato);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `capa_conciliacao_${item.id}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage(extrairMensagemErro(err, 'Não foi possível exportar a capa da conciliação.'));
    }
  };

  if (loadingDetalhe) return <div className='wf-page'><Header title='Carregando...' subtitle='Buscando dados da conciliação.' /></div>;
  if (!item) return <div className='wf-page'><Header title='Conciliação não encontrada' subtitle='O registro não existe ou você não tem acesso.' /></div>;

  const problems = item.steps.reduce<string[]>((acc, step) => {
    if (step.erros) acc.push(`${step.nome}: ${step.erros} erro(s)`);
    if (step.pendencias) acc.push(`${step.nome}: ${step.pendencias} pendência(s)`);
    return acc;
  }, []);
  // EM_CONFERENCIA fica de fora: aguarda o complemento pedido, resolvido por
  // resolverComplemento (reenvia pro aprovador real da etapa) — não por "Aprovar e
  // avançar", que avançaria a etapa como se fosse a aprovação daquela etapa em si.
  const souAutor = souAutorDaConciliacao(item.autorId, meuUserId);
  const canApprove = !souAutor && problems.length === 0 && item.status !== 'BLOQUEADA' && item.status !== 'APROVADA' && item.status !== 'EM_CONFERENCIA';
  const currentStep = item.steps.find(s => s.id === activeStep);
  const isEtapaFinal = item.etapaAtual >= 6;
  const proximaEtapa = isEtapaFinal ? null : item.steps.find(s => s.id === item.etapaAtual + 1);
  const isEtapaInicial = item.etapaAtual <= 1;
  const etapaAnterior = isEtapaInicial ? null : item.steps.find(s => s.id === item.etapaAtual - 1);

  // Volta pra lista já com a mesma empresa/período desta conciliação selecionados — evita
  // ter que escolher tudo de novo depois de aprovar/devolver.
  const voltarParaLista = () => history.push('/workflow-aprovacao/conciliacoes', {
    empresaId: String(item.empresaId),
    periodo: item.periodo,
  });

  const approve = async () => {
    if (!canApprove) return;
    setApproving(true);
    try {
      await workflowService.aprovarConciliacao(item.id, justificativa || undefined);
      setShowApproveConfirm(false);
      voltarParaLista();
    } catch (err: any) {
      setApproving(false);
      setMessage(extrairMensagemErro(err, 'Erro ao aprovar. Verifique as permissões e tente novamente.'));
    }
  };

  const reject = async () => {
    if (!reason.trim()) { setMessage('Informe o motivo da rejeição.'); return; }
    setRejecting(true);
    try {
      await workflowService.rejeitarConciliacao(item.id, reason);
      setShowReject(false);
      setReason('');
      voltarParaLista();
    } catch (err: any) {
      setRejecting(false);
      setMessage(extrairMensagemErro(err, 'Erro ao devolver. Tente novamente.'));
    }
  };

  const solicitarComplemento = async () => {
    if (!motivoDevolucao) { setMessage('Selecione o motivo da devolução.'); return; }
    setLoadingSolicitar(true);
    try {
      await workflowService.solicitarComplemento(item.id, {
        motivoDevolucao,
        observacao: observacaoSolicitar || undefined,
      });
      setShowSolicitar(false);
      setMotivoDevolucao('');
      setObservacaoSolicitar('');
      voltarParaLista();
    } catch (err: any) {
      setLoadingSolicitar(false);
      setMessage(extrairMensagemErro(err, 'Erro ao solicitar complemento. Verifique os dados e tente novamente.'));
    }
  };

  // Sinaliza que o complemento pedido foi atendido — reenvia pro aprovador real da etapa
  // atual. Distinto de aprovar: não avança etapa, só devolve pra fila de quem precisa
  // aprovar de fato (evita pular a aprovação de quem tem a alçada daquela etapa).
  const resolverComplemento = async () => {
    setResolvendoComplemento(true);
    try {
      await workflowService.resolverComplemento(item.id, observacaoComplemento || undefined);
      setShowResolverComplemento(false);
      setObservacaoComplemento('');
      voltarParaLista();
    } catch (err: any) {
      setResolvendoComplemento(false);
      setMessage(extrairMensagemErro(err, 'Erro ao reenviar. Tente novamente.'));
    }
  };

  return <div className='wf-page wf-detail'>
    <Header title={`${item.empresa} - ${item.conta} - ${item.descricaoConta}`} subtitle={`Período ${item.periodo}`} actions={<>
      {item.status === 'APROVADA' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => exportar('pdf')}>
            ↓ PDF
          </button>
          <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => exportar('excel')}>
            ↓ Excel
          </button>
        </div>
      )}
      <button className='wf-btn wf-btn--secondary' onClick={() => item.cartaoId ? window.open(`/capa-conciliacao/${item.cartaoId}`, '_blank') : history.push(`/workflow-aprovacao/conciliacoes/${item.id}/capa`)}>Capa da conta contábil</button>
      <button className='wf-btn wf-btn--secondary' title='Abre somente a capa SOX desta conciliação em outra aba, para imprimir ou salvar em PDF' onClick={() => abrirCapaParaImpressao(item.id)}>🖨 Imprimir capa</button>
      <button className='wf-btn wf-btn--secondary' onClick={voltarParaLista}>Voltar à lista</button>
      <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>
    </>} />
    {message && <div className='wf-message'>{message}</div>}

    {item.status === 'EM_CONFERENCIA' && item.motivoDevolucao && (
      <div className='wf-review' style={{ borderLeft: '4px solid #f6c23e', background: '#fffbeb', marginBottom: 16, padding: '14px 18px' }}>
        <strong>⚠ Aviso de retorno — conta devolvida pra complementação</strong>
        <p style={{ margin: '6px 0 0' }}>
          Esta conciliação voltou para {item.devolvidoParaNome ? <strong>{item.devolvidoParaNome}</strong> : 'o profissional responsável'} complementar antes de seguir no fluxo.
        </p>
        <p style={{ margin: '4px 0 0' }}><strong>Motivo:</strong> {
          item.motivoDevolucao === 'DOCUMENTACAO_INCOMPLETA' ? 'Documentação Incompleta' :
          item.motivoDevolucao === 'DIVERGENCIA_NAO_JUSTIFICADA' ? 'Divergência Não Justificada' :
          'Necessidade de Evidência Complementar'
        }</p>
      </div>
    )}
    <Card><div className='wf-steps'>{item.steps.map((step, index) => <React.Fragment key={step.id}>
      <button disabled={step.status === 'NAO_INICIADA'} className={`wf-step wf-step--${step.status.toLowerCase()} ${activeStep === step.id ? 'is-active' : ''}`} onClick={() => setActiveStep(step.id)}>
        <span>{step.status === 'CONCLUIDA' ? '✓' : step.status === 'REJEITADA' ? '×' : step.status === 'COM_ERRO' ? '!' : step.status === 'EM_ANDAMENTO' ? '●' : '○'}</span>
        <strong>{step.nome}</strong>
        {(step.erros > 0 || step.pendencias > 0) && <small>{step.erros > 0 ? `${step.erros} erro(s)` : `${step.pendencias} pend.`}</small>}
      </button>
      {index < item.steps.length - 1 && <div className={`wf-step-line ${step.status === 'CONCLUIDA' ? 'is-done' : ''}`} />}
    </React.Fragment>)}</div></Card>

    <div className='wf-detail-grid'>
      <main>
        <Card title={currentStep?.nome || 'Etapa'} subtitle='Dados, validações e evidências desta etapa.'>
          {currentStep?.comentario && <div className='wf-review'><strong>Motivo apontado pelo revisor</strong><p>{currentStep.comentario}</p></div>}
          <div className='wf-form-grid'>
            <label>Empresa<input value={item.empresa} readOnly /></label><label>CNPJ<input value={item.cnpj} readOnly /></label>
            <label>Conta contábil<input value={item.conta} readOnly /></label><label>Descrição<input value={item.descricaoConta} readOnly /></label>
            <label>Período<input value={item.periodo} readOnly /></label><label>Centro de custo<input value='Corporativo / Controladoria' readOnly /></label>
          </div>
          <div className='wf-values'><div><span>Saldo contábil</span><strong>{money(item.saldoContabil)}</strong></div><div><span>Saldo conciliado</span><strong>{money(item.saldoConciliado)}</strong></div><div className={item.divergencia ? 'is-danger' : ''}><span>Divergência</span><strong>{money(item.divergencia)}</strong></div></div>
          <div className='wf-form-grid wf-form-grid--one'><label>Justificativa detalhada<textarea readOnly value={item.justificativaDetalhada ?? ''} placeholder='Nenhuma justificativa informada pelo responsável.' rows={4} /></label></div>
          <div className='wf-evidence'><div><strong>Evidências anexadas</strong><span>{item.evidencias.length > 0 ? `${item.evidencias.length} arquivo(s) vinculado(s)` : 'Nenhum arquivo anexado'}</span></div><div>{item.evidencias.map(e => <em key={e.evidenciaId}>{e.nomeArquivo}</em>)}</div></div>
          <div className='wf-validation'><strong>Validação de integridade</strong>{problems.length ? <><p>O avanço está bloqueado:</p><ul>{problems.map(p => <li key={p}>{p}</li>)}</ul></> : <p className='wf-success-text'>Todas as etapas estão íntegras.</p>}</div>
        </Card>
      </main>
      <aside>
        <Card title='Resumo da aprovação'>
          <div className='wf-side-list'><div><span>Status</span><StatusBadge status={item.status} /></div><div><span>Responsável atual</span><strong>{item.responsavelAtual}</strong></div><div><span>Etapa atual</span><strong>{item.nomeEtapa || `Passo ${item.nivelAtual} de 6`}</strong></div><div><span>SLA</span><strong>{item.sla}</strong></div><div><span>Autor / Maker</span><strong>{item.autor}</strong></div><div><span>Risco</span><RiskBadge risk={item.risco} /></div><div><span>Hash de integridade</span><code>{item.hashIntegridade}</code></div></div>
        </Card>
        <Card title='Cadeia de aprovação'>
          <div className='wf-approvers'>
            {item.steps.map(step => {
              const cargoLabel = step.cargo ?? step.nome;
              if (step.status === 'CONCLUIDA') return <div className='wf-approver wf-approver--done' key={step.id}>
                <div className='wf-approver__icon'>•</div>
                <div className='wf-approver__body'>
                  <strong>{cargoLabel}</strong>
                  <span>{step.responsavel}</span>
                  <small>{step.concluidoEm ? new Date(step.concluidoEm).toLocaleString('pt-BR') : ''}</small>
                  {step.autenticador && <code>Auth: {step.autenticador}</code>}
                </div>
              </div>;
              if (step.status === 'REJEITADA') return <div className='wf-approver wf-approver--rejected' key={step.id}>
                <div className='wf-approver__icon'>•</div>
                <div className='wf-approver__body'>
                  <strong>{cargoLabel} — Recusou</strong>
                  <span>{step.responsavel}</span>
                  {step.comentario && <p>{step.comentario}</p>}
                </div>
              </div>;
              if (step.status === 'EM_ANDAMENTO') return <div className='wf-approver wf-approver--active' key={step.id}>
                <div className='wf-approver__icon'>•</div>
                <div className='wf-approver__body'>
                  <strong>{cargoLabel}</strong>
                  <span>{step.responsavel} — aguardando</span>
                </div>
              </div>;
              return <div className='wf-approver wf-approver--pending' key={step.id}>
                <div className='wf-approver__icon'>•</div>
                <div className='wf-approver__body'>
                  <strong>{cargoLabel}</strong>
                  <span>{step.responsavel}</span>
                </div>
              </div>;
            })}
          </div>
        </Card>
        <Card title='Segregação de funções'>{souAutor
          ? <div className='wf-sod wf-sod--alerta' role='alert'><b>!</b><div><strong>Você não pode aprovar esta conciliação</strong><p>{MENSAGEM_AUTOR_NAO_APROVA}</p></div></div>
          : <div className='wf-sod'><b>✓</b><div><strong>SoD validada</strong><p>O autor não é o aprovador atual.</p></div></div>}</Card>
      </aside>
    </div>

    <section className='wf-card' style={{ marginTop: 16 }}>
      <div className='wf-card__header'>
        <div><h2>Justificativa Técnica</h2><p>Descreva a análise da conta para fins de auditoria SOX.</p></div>
        <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={salvarJustificativa}>Salvar</button>
      </div>
      <textarea
        className='wf-input'
        rows={5}
        value={justificativa}
        onChange={e => setJustificativa(e.target.value)}
        placeholder='Descreva a conciliação, os saldos e o racional contábil...'
        style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 8, border: '1px solid #dfe3ea', fontSize: 13 }}
      />
    </section>

    <section className='wf-card' style={{ marginTop: 16 }}>
      <div className='wf-card__header'>
        <div><h2>Evidências</h2><p>Anexos de suporte à conciliação.</p></div>
      </div>
      <input type='file' multiple onChange={handleUpload} style={{ marginBottom: 12 }} />
      {evidencias.map(ev => (
        <div key={ev.evidenciaId} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1f3f5', fontSize: 12 }}>
          <span>📎</span>
          <span style={{ flex: 1 }}>{ev.nomeArquivo}</span>
          <span style={{ color: '#a1a5b7' }}>{(ev.tamanhoBytes / 1024).toFixed(0)}KB</span>
          <a href={ev.urlStorage} target='_blank' rel='noreferrer' className='wf-link'>Ver</a>
        </div>
      ))}
    </section>

    <PlanoMelhoriaSection concilId={item.id} />

    <HistoricoSoxSection concilId={item.id} />

    <div className='wf-actions'>
      <button className='wf-btn wf-btn--secondary' onClick={() => setMessage('Rascunho salvo automaticamente.')}>Salvar rascunho</button>
      <div style={{ display: 'flex', gap: 8 }}>
        {item.status === 'EM_CONFERENCIA' ? (
          <button className='wf-btn wf-btn--primary' disabled={resolvendoComplemento} onClick={() => setShowResolverComplemento(true)}>
            Reenviar após complemento
          </button>
        ) : (
          <>
            <button className='wf-btn wf-btn--danger-outline' onClick={() => setShowReject(true)}>Solicitar correção</button>
            <button className='wf-btn wf-btn--warning' onClick={() => setShowSolicitar(true)} title='Devolver ao Aprovador do Módulo de Conciliação para complementação'>
              Solicitar Complemento (SOX)
            </button>
            <button className='wf-btn wf-btn--primary' disabled={!canApprove || approving} title={souAutor ? MENSAGEM_AUTOR_NAO_APROVA : undefined} onClick={() => setShowApproveConfirm(true)}>Aprovar e avançar</button>
          </>
        )}
      </div>
    </div>

    {showReject && <div className='wf-modal-bg'><div className='wf-modal'><div className='wf-modal__header'><div><h2>Solicitar correção</h2><p>
      {isEtapaInicial
        ? 'A conciliação será devolvida ao autor para corrigir o cadastro (Nível 1).'
        : `A conciliação voltará automaticamente para a etapa anterior: ${etapaAnterior?.nome ?? `Nível ${item.etapaAtual - 1}`}.`}
    </p></div><button onClick={() => setShowReject(false)}>×</button></div><div className='wf-modal__body'><label>Motivo<textarea rows={5} value={reason} onChange={e => setReason(e.target.value)} placeholder='Descreva o que precisa ser corrigido...' /></label></div><div className='wf-modal__footer'><button className='wf-btn wf-btn--secondary' onClick={() => setShowReject(false)} disabled={rejecting}>Cancelar</button><button className='wf-btn wf-btn--danger' onClick={reject} disabled={rejecting}>{rejecting ? 'Devolvendo...' : 'Confirmar devolução'}</button></div></div></div>}

    {showApproveConfirm && (
      <div className='wf-modal-bg'>
        <div className='wf-modal'>
          <div className='wf-modal__header'>
            <div>
              <h2>Confirmar aprovação</h2>
              <p>
                {isEtapaFinal
                  ? 'Esta é a etapa final do fluxo — a conciliação será aprovada e encerrada.'
                  : `A conciliação será enviada para a próxima etapa: ${proximaEtapa?.nome ?? `Nível ${item.etapaAtual + 1}`}.`}
              </p>
            </div>
            <button onClick={() => setShowApproveConfirm(false)}>×</button>
          </div>
          <div className='wf-modal__body'>
            <p style={{ fontSize: 13, color: '#5e6278' }}>
              <strong>{item.empresa}</strong> — {item.conta} {item.descricaoConta && `— ${item.descricaoConta}`}
            </p>
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#7e8299', marginTop: 8 }}>
              🔒 Esta ação será registrada no log imutável de Auditoria SOX.
            </div>
          </div>
          <div className='wf-modal__footer'>
            <button className='wf-btn wf-btn--secondary' onClick={() => setShowApproveConfirm(false)} disabled={approving}>Cancelar</button>
            <button className='wf-btn wf-btn--primary' onClick={approve} disabled={approving}>
              {approving ? 'Aprovando...' : 'Confirmar aprovação'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showSolicitar && (
      <div className='wf-modal-bg'>
        <div className='wf-modal'>
          <div className='wf-modal__header'>
            <div>
              <h2>Solicitar Complemento (SOX)</h2>
              <p>O processo será roteado ao Aprovador do Módulo de Conciliação.</p>
            </div>
            <button onClick={() => { setShowSolicitar(false); setMotivoDevolucao(''); setObservacaoSolicitar(''); }}>×</button>
          </div>
          <div className='wf-modal__body'>
            <label>
              Motivo da Devolução <span style={{ color: '#e74a3b' }}>*</span>
              <select
                value={motivoDevolucao}
                onChange={e => setMotivoDevolucao(e.target.value as MotivoDevolucao)}
                style={{ marginTop: 6, width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #dfe3ea' }}
              >
                <option value=''>Selecione o motivo...</option>
                {MOTIVOS_DEVOLUCAO.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </label>
            <label style={{ marginTop: 12, display: 'block' }}>
              Observação complementar <span style={{ color: '#a1a5b7', fontSize: 12 }}>(opcional)</span>
              <textarea
                rows={4}
                value={observacaoSolicitar}
                onChange={e => setObservacaoSolicitar(e.target.value)}
                placeholder='Descreva detalhes adicionais sobre a pendência...'
                style={{ marginTop: 6, width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #dfe3ea', resize: 'vertical' }}
              />
            </label>
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8f9fa', borderRadius: 6, fontSize: 12, color: '#5a5c69' }}>
              <strong>O que acontece após confirmar:</strong>
              <ol style={{ margin: '6px 0 0 16px' }}>
                <li>Status muda para <strong>Aguardando complemento</strong></li>
                <li>Card é alocado na mesa do Aprovador do Módulo de Conciliação</li>
                <li>E-mail automático é disparado ao aprovador e ao analista responsável</li>
                <li>Após complementação, o processo retorna ao Workflow SOX</li>
              </ol>
            </div>
          </div>
          <div className='wf-modal__footer'>
            <button className='wf-btn wf-btn--secondary' onClick={() => { setShowSolicitar(false); setMotivoDevolucao(''); setObservacaoSolicitar(''); }}>
              Cancelar
            </button>
            <button
              className='wf-btn wf-btn--warning'
              disabled={!motivoDevolucao || loadingSolicitar}
              onClick={solicitarComplemento}
            >
              {loadingSolicitar ? 'Enviando...' : 'Confirmar Devolução'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showResolverComplemento && (
      <div className='wf-modal-bg'>
        <div className='wf-modal'>
          <div className='wf-modal__header'>
            <div>
              <h2>Reenviar após complemento</h2>
              <p>
                A conciliação volta para a fila de quem realmente precisa aprovar a etapa atual
                ({item.nomeEtapa || `passo ${item.nivelAtual} de 6`}) — isso <strong>não conta</strong> como
                a aprovação dessa etapa, só devolve pra revisão de quem tem a alçada.
              </p>
            </div>
            <button onClick={() => { setShowResolverComplemento(false); setObservacaoComplemento(''); }}>×</button>
          </div>
          <div className='wf-modal__body'>
            <label>
              O que foi corrigido/complementado <span style={{ color: '#a1a5b7', fontSize: 12 }}>(opcional)</span>
              <textarea
                rows={4}
                value={observacaoComplemento}
                onChange={e => setObservacaoComplemento(e.target.value)}
                placeholder='Descreva o que foi ajustado para atender à solicitação...'
                style={{ marginTop: 6, width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #dfe3ea', resize: 'vertical' }}
              />
            </label>
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#7e8299', marginTop: 8 }}>
              🔒 Esta ação será registrada no log imutável de Auditoria SOX.
            </div>
          </div>
          <div className='wf-modal__footer'>
            <button className='wf-btn wf-btn--secondary' onClick={() => { setShowResolverComplemento(false); setObservacaoComplemento(''); }} disabled={resolvendoComplemento}>
              Cancelar
            </button>
            <button className='wf-btn wf-btn--primary' onClick={resolverComplemento} disabled={resolvendoComplemento}>
              {resolvendoComplemento ? 'Reenviando...' : 'Confirmar reenvio'}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>;
};

export const PendenciasPage: React.FC = () => {
  const history = useHistory();
  const [conciliacoes, setConciliacoes] = useState<WorkflowConciliacao[]>([]);
  useEffect(() => { workflowService.listarMinhasPendencias().then(setConciliacoes).catch(() => {}); }, []);
  return <div className='wf-page'>
    <Header title='Minhas Pendências' subtitle='Itens atribuídos a você ou devolvidos para correção.' actions={<button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>} />
    <Metrics />
    <Card title='Fila de trabalho' subtitle='Ordenada por risco e vencimento de SLA.'><ConciliationTable items={conciliacoes} /></Card>
  </div>;
};

const lancamentoVazio = {
  empresaId: '', periodo: '', contaContabil: '', tipo: 'DEBITO' as 'CREDITO' | 'DEBITO',
  dataLancamento: new Date().toISOString().slice(0, 10), valor: '', historico: '',
  riscoCompliance: 'MEDIO' as 'BAIXO' | 'MEDIO' | 'ALTO', justificativaDetalhada: '', planoAcaoCorretiva: '',
};

type PeriodoComEsteira = PeriodoOpt & { esteiraId: number };

// Select pesquisável genérico (react-select), reaproveitando o mesmo estilo já usado no
// FilterBar do dashboard — evita ter que rolar listas grandes (ex.: centenas de contas).
const BuscaSelect: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  noOptionsMessage?: string;
}> = ({ options, value, onChange, placeholder, disabled, noOptionsMessage }) => (
  <Select
    options={options}
    value={options.find(o => o.value === value) ?? null}
    onChange={(opt: any) => onChange(opt?.value ?? '')}
    isDisabled={disabled}
    isClearable
    placeholder={placeholder}
    menuPortalTarget={document.body}
    menuPosition='fixed'
    styles={empresaSelectStyles}
    noOptionsMessage={() => noOptionsMessage ?? 'Nenhum resultado'}
  />
);

export const LancamentosManuaisPage: React.FC = () => {
  const history = useHistory();
  const locationLanc = useLocation<{ empresaId?: string; periodo?: string } | undefined>();
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);

  // Filtro da lista — mesmo padrão de empresa+período das demais telas do módulo, evita
  // varrer todas as empresas do usuário de uma vez quando há muitas cadastradas.
  const [filtroEmpresaId, setFiltroEmpresaId] = useState(locationLanc.state?.empresaId ?? '');
  const [periodosFiltro, setPeriodosFiltro] = useState<PeriodoComEsteira[]>([]);
  const [filtroPeriodo, setFiltroPeriodo] = useState(locationLanc.state?.periodo ?? '');
  const [entries, setEntries] = useState<ManualEntry[]>([]);
  const [carregandoLista, setCarregandoLista] = useState(false);

  const [selected, setSelected] = useState<ManualEntry | null>(null);
  const [acaoErro, setAcaoErro] = useState('');
  const [processando, setProcessando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [erroExport, setErroExport] = useState('');

  const [showNovo, setShowNovo] = useState(false);
  const [periodosNovo, setPeriodosNovo] = useState<PeriodoComEsteira[]>([]);
  const [contas, setContas] = useState<MatrizContaContabil[]>([]);
  const [form, setForm] = useState(lancamentoVazio);
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState('');

  useEffect(() => {
    workflowService.listarTenants()
      .then(lista => setEmpresas(lista.filter(e => e.grupoId != null && e.status === 'ATIVA')))
      .catch(() => setEmpresas([]));
  }, []);

  useEffect(() => {
    if (!filtroEmpresaId) { setPeriodosFiltro([]); setFiltroPeriodo(''); return; }
    const emp = empresas.find(e => String(e.id) === filtroEmpresaId);
    if (emp?.empresaId) {
      workflowService.getPeriodosEmpresa(emp.empresaId)
        .then(ps => setPeriodosFiltro(ps.map(p => ({ value: periodoValueFromIso(p.periodoInicio), label: p.label, esteiraId: p.esteiraId }))))
        .catch(() => setPeriodosFiltro([]));
    }
  }, [filtroEmpresaId, empresas]);

  // Filtra pela esteira real da conta (resolvida do período escolhido), não pela data
  // digitada no lançamento — a data é só um registro de quando foi feito, quem determina de
  // qual período contábil o lançamento é de fato é a esteira.
  const esteiraFiltro = periodosFiltro.find(p => p.value === filtroPeriodo)?.esteiraId;

  useEffect(() => {
    if (!filtroEmpresaId || !filtroPeriodo || !esteiraFiltro) { setEntries([]); return; }
    setCarregandoLista(true);
    workflowService.listarLancamentos(Number(filtroEmpresaId), esteiraFiltro)
      .then(setEntries)
      .catch(() => setEntries([]))
      .finally(() => setCarregandoLista(false));
  }, [filtroEmpresaId, filtroPeriodo, esteiraFiltro]);

  const recarregarLista = () => {
    if (!filtroEmpresaId || !filtroPeriodo || !esteiraFiltro) return;
    workflowService.listarLancamentos(Number(filtroEmpresaId), esteiraFiltro).then(setEntries).catch(() => {});
  };

  useEffect(() => {
    if (!form.empresaId) { setPeriodosNovo([]); setContas([]); return; }
    const emp = empresas.find(e => String(e.id) === form.empresaId);
    if (emp?.empresaId) {
      workflowService.getPeriodosEmpresa(emp.empresaId)
        .then(ps => setPeriodosNovo(ps.map(p => ({ value: periodoValueFromIso(p.periodoInicio), label: p.label, esteiraId: p.esteiraId }))))
        .catch(() => setPeriodosNovo([]));
    }
    workflowService.matrizContas(Number(form.empresaId))
      .then(lista => setContas(lista.filter(c => c.tipo === 'ANALITICA')))
      .catch(() => setContas([]));
  }, [form.empresaId, empresas]);

  // Abre o modal já com a empresa/período do filtro da lista preenchidos, já que
  // provavelmente é para essa empresa/período que a pessoa quer lançar.
  const abrirNovo = () => {
    setForm({ ...lancamentoVazio, empresaId: filtroEmpresaId, periodo: filtroPeriodo });
    setErroForm('');
    setShowNovo(true);
  };

  const setField = (key: keyof typeof lancamentoVazio, value: string) =>
    setForm(f => ({ ...f, [key]: value }));

  const salvarLancamento = async () => {
    setErroForm('');
    if (!form.empresaId || !form.periodo) { setErroForm('Selecione a empresa e o período.'); return; }
    if (!form.contaContabil) { setErroForm('Selecione a conta contábil.'); return; }
    if (!form.valor || Number(form.valor) <= 0) { setErroForm('Informe um valor válido.'); return; }
    if (!form.historico.trim()) { setErroForm('Informe o histórico do lançamento.'); return; }
    if (!form.justificativaDetalhada.trim() || !form.planoAcaoCorretiva.trim()) {
      setErroForm('Justificativa e plano de ação corretiva são obrigatórios (política SOX).');
      return;
    }
    const periodoOpt = periodosNovo.find(p => p.value === form.periodo);
    const esteiraId = periodoOpt?.esteiraId ?? 0;
    if (!esteiraId) { setErroForm('Não foi possível identificar o período selecionado. Selecione novamente.'); return; }
    setSalvando(true);
    try {
      await workflowService.criarLancamento({
        tenantId: Number(form.empresaId),
        esteiraId,
        dataLancamento: form.dataLancamento,
        contaContabil: form.contaContabil,
        historico: form.historico,
        valor: Number(form.valor),
        tipo: form.tipo,
        riscoCompliance: form.riscoCompliance,
        justificativaDetalhada: form.justificativaDetalhada,
        planoAcaoCorretiva: form.planoAcaoCorretiva,
      });
      setShowNovo(false);
      if (form.empresaId === filtroEmpresaId && form.periodo === filtroPeriodo) recarregarLista();
    } catch (err) {
      setErroForm(extrairMensagemErro(err, 'Não foi possível salvar o lançamento.'));
    } finally {
      setSalvando(false);
    }
  };

  const aprovar = async () => {
    if (!selected) return;
    setProcessando(true); setAcaoErro('');
    try {
      const atualizado = await workflowService.aprovarLancamento(selected.id);
      setSelected(atualizado);
      recarregarLista();
    } catch (err) {
      setAcaoErro(extrairMensagemErro(err, 'Não foi possível aprovar.'));
    } finally {
      setProcessando(false);
    }
  };

  const rejeitar = async () => {
    if (!selected) return;
    const motivo = window.prompt('Motivo da rejeição:');
    if (!motivo) return;
    setProcessando(true); setAcaoErro('');
    try {
      const atualizado = await workflowService.rejeitarLancamento(selected.id, motivo);
      setSelected(atualizado);
      recarregarLista();
    } catch (err) {
      setAcaoErro(extrairMensagemErro(err, 'Não foi possível rejeitar.'));
    } finally {
      setProcessando(false);
    }
  };

  // Exporta com o filtro da tela (empresa/período) quando há; sem filtro, todos os lançamentos que o usuário enxerga.
  const exportarLancamentos = async () => {
    setExportando(true); setErroExport('');
    try {
      const blob = await workflowService.exportarLancamentos(filtroEmpresaId ? Number(filtroEmpresaId) : undefined, esteiraFiltro);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lancamentos-manuais-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErroExport(extrairMensagemErro(err, 'Não foi possível exportar os lançamentos manuais.'));
    } finally {
      setExportando(false);
    }
  };

  const empresaOptions = empresas.map(e => ({ value: String(e.id), label: e.nome }));
  const isReady = !!filtroEmpresaId && !!filtroPeriodo;
  const alto = entries.filter(e => e.risco === 'ALTO').length;
  const semPlano = entries.filter(e => !e.planoAcao).length;
  const emAprovacao = entries.filter(e => e.status === 'PENDENTE').length;
  const aging30 = entries.filter(e => e.agingDias > 30).length;
  return <div className='wf-page'><Header title='Lançamentos Manuais' subtitle='Governança, justificativas, plano de ação e aprovação de lançamentos manuais.' actions={<><button className='wf-btn wf-btn--primary' onClick={abrirNovo}>+ Novo Lançamento</button><button className='wf-btn wf-btn--secondary' onClick={exportarLancamentos} disabled={exportando}>{exportando ? 'Exportando...' : 'Exportar CSV'}</button><button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button></>} />
  {erroExport && <div className='wf-review' role='alert'><strong>Erro ao exportar</strong><p>{erroExport}</p></div>}
    <Card>
      <div className='wf-form-grid'>
        <label>Empresa<BuscaSelect options={empresaOptions} value={filtroEmpresaId} onChange={v => { setFiltroEmpresaId(v); setFiltroPeriodo(''); }} placeholder='Buscar empresa...' noOptionsMessage='Nenhuma empresa encontrada' /></label>
        <label>Período<BuscaSelect options={periodosFiltro} value={filtroPeriodo} onChange={setFiltroPeriodo} placeholder={!filtroEmpresaId ? 'Selecione a empresa primeiro' : '— Selecione —'} disabled={!filtroEmpresaId || periodosFiltro.length === 0} /></label>
      </div>
    </Card>
    {!isReady ? (
      <Card><p className='wf-count' style={{ textAlign: 'center', padding: 24 }}>
        {!filtroEmpresaId ? 'Selecione uma empresa para ver os lançamentos manuais.' : 'Selecione o período.'}
      </p></Card>
    ) : <>
    <div className='wf-metrics wf-metrics--five'>
    {[[`Total manual`,String(entries.length),'No período','primary'],[`Alto risco`,String(alto),'Prioridade','danger'],[`Sem plano de ação`,String(semPlano),'Salvamento bloqueado','warning'],[`Aguardando aprovação`,String(emAprovacao),'Níveis 2 a 6','info'],[`Aging > 30 dias`,String(aging30),'Risco de recorrência','dark']].map(([a,b,c,d]) => <div key={a} className={`wf-metric wf-metric--${d}`}><span>{a}</span><strong>{b}</strong><small>{c}</small></div>)}
    </div>
    <Card><div className='wf-table-wrap'><table className='wf-table'><thead><tr><th>Data</th><th>Empresa / Conta</th><th>Histórico</th><th>Valor</th><th>Autor</th><th>Risco</th><th>Status</th><th /></tr></thead><tbody>{carregandoLista ? <tr><td colSpan={8} style={{textAlign:'center',color:'#a1a5b7',padding:24}}>Carregando...</td></tr> : entries.length === 0 ? <tr><td colSpan={8} style={{textAlign:'center',color:'#a1a5b7',padding:24}}>Nenhum lançamento manual encontrado.</td></tr> : entries.map(e => <tr key={e.id}><td>{e.data}</td><td><strong>{e.empresa}</strong><span>{e.conta}</span></td><td>{e.historico}</td><td>{money(e.valor)}</td><td>{e.autor}</td><td><RiskBadge risk={e.risco} /></td><td><StatusBadge status={e.status} /></td><td><button className='wf-link' onClick={() => { setSelected(e); setAcaoErro(''); }}>Detalhes</button></td></tr>)}</tbody></table></div></Card>
    </>}
  {selected && <div className='wf-modal-bg'><div className='wf-modal wf-modal--wide'><div className='wf-modal__header'><div><h2>Lançamento manual #{selected.id}</h2><p>{selected.empresa}</p></div><button onClick={() => setSelected(null)}>×</button></div><div className='wf-modal__body'><div className='wf-kpis'><div><span>Valor</span><strong>{money(selected.valor)}</strong></div><div><span>Autor</span><strong>{selected.autor}</strong></div><div><span>Aging</span><strong>{selected.agingDias} dias</strong></div><div><span>Risco</span><RiskBadge risk={selected.risco} /></div></div><label>Justificativa<textarea readOnly rows={4} value={selected.justificativa || 'Não informada'} /></label><label>Plano de ação<textarea readOnly rows={4} value={selected.planoAcao || 'Não informado'} /></label>{!selected.planoAcao && <div className='wf-review'><strong>Bloqueio de compliance</strong><p>O lançamento não pode avançar sem plano de ação.</p></div>}{acaoErro && <div className='wf-review'><strong>Erro</strong><p>{acaoErro}</p></div>}</div><div className='wf-modal__footer'>
    {selected.cartaoId && <button className='wf-btn wf-btn--secondary' onClick={() => window.open(`/capa-conciliacao/${selected.cartaoId}`, '_blank')}>Ver Capa</button>}
    <button className='wf-btn wf-btn--secondary' onClick={() => setSelected(null)}>Fechar</button>
    {selected.status === 'PENDENTE' && <>
      <button className='wf-btn wf-btn--danger' disabled={processando} onClick={rejeitar}>Rejeitar</button>
      <button className='wf-btn wf-btn--primary' disabled={!selected.planoAcao || processando} onClick={aprovar}>Aprovar</button>
    </>}
  </div></div></div>}
  {showNovo && <div className='wf-modal-bg'><div className='wf-modal wf-modal--wide'><div className='wf-modal__header'><div><h2>Novo lançamento manual</h2><p>Entra imediatamente como pendente na Capa de Conciliação da conta escolhida.</p></div><button onClick={() => setShowNovo(false)}>×</button></div><div className='wf-modal__body'>
    <div className='wf-form-grid'>
      <label>Empresa<BuscaSelect options={empresaOptions} value={form.empresaId} onChange={v => setForm(f => ({ ...f, empresaId: v, periodo: '', contaContabil: '' }))} placeholder='Buscar empresa...' noOptionsMessage='Nenhuma empresa encontrada' /></label>
      <label>Período<BuscaSelect options={periodosNovo} value={form.periodo} onChange={v => setField('periodo', v)} placeholder={!form.empresaId ? 'Selecione a empresa primeiro' : '— Selecione —'} disabled={!form.empresaId || periodosNovo.length === 0} /></label>
      <label>Conta contábil<BuscaSelect options={contas.map(c => ({ value: c.codigo, label: `${c.codigo} — ${c.descricao}` }))} value={form.contaContabil} onChange={v => setField('contaContabil', v)} placeholder={!form.empresaId ? 'Selecione a empresa primeiro' : 'Buscar conta...'} disabled={!form.empresaId || contas.length === 0} noOptionsMessage='Nenhuma conta encontrada' /></label>
      <label>Tipo<select value={form.tipo} onChange={e => setField('tipo', e.target.value)}>
        <option value='DEBITO'>Débito (D)</option>
        <option value='CREDITO'>Crédito (C)</option>
      </select></label>
      <label>Data do lançamento<input type='date' value={form.dataLancamento} onChange={e => setField('dataLancamento', e.target.value)} /></label>
      <label>Valor<input type='number' min='0' step='0.01' value={form.valor} onChange={e => setField('valor', e.target.value)} /></label>
      <label>Risco de compliance<select value={form.riscoCompliance} onChange={e => setField('riscoCompliance', e.target.value)}>
        <option value='BAIXO'>Baixo</option>
        <option value='MEDIO'>Médio</option>
        <option value='ALTO'>Alto</option>
      </select></label>
    </div>
    <div className='wf-form-grid wf-form-grid--one'>
      <label>Histórico<input value={form.historico} onChange={e => setField('historico', e.target.value)} placeholder='Descreva o lançamento...' /></label>
      <label>Justificativa detalhada<textarea rows={3} value={form.justificativaDetalhada} onChange={e => setField('justificativaDetalhada', e.target.value)} placeholder='Por que esse lançamento manual é necessário...' /></label>
      <label>Plano de ação corretiva<textarea rows={3} value={form.planoAcaoCorretiva} onChange={e => setField('planoAcaoCorretiva', e.target.value)} placeholder='Como evitar a necessidade desse ajuste manual no futuro...' /></label>
    </div>
    {erroForm && <div className='wf-review'><strong>Não foi possível salvar</strong><p>{erroForm}</p></div>}
  </div><div className='wf-modal__footer'><button className='wf-btn wf-btn--secondary' onClick={() => setShowNovo(false)} disabled={salvando}>Cancelar</button><button className='wf-btn wf-btn--primary' onClick={salvarLancamento} disabled={salvando}>{salvando ? 'Salvando...' : 'Lançar'}</button></div></div></div>}
  </div>;
};

export const PendenciasTransversaisPage: React.FC = () => {
  const history = useHistory();
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);
  const [empresaId, setEmpresaId] = useState('');
  const [periodos, setPeriodos] = useState<PeriodoOpt[]>([]);
  const [periodo, setPeriodo] = useState('');
  const [dados, setDados] = useState<PendenciasTransversaisResponse>({
    dividaTecnicaAtual: 0, abertas: 0, herdadas: 0, vencidas: 0, agingMedioDias: 0, itens: [],
  });
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    workflowService.listarTenants()
      .then(lista => setEmpresas(lista.filter(e => e.grupoId != null && e.status === 'ATIVA')))
      .catch(() => setEmpresas([]));
  }, []);

  useEffect(() => {
    if (!empresaId) { setPeriodos([]); setPeriodo(''); return; }
    const emp = empresas.find(e => String(e.id) === empresaId);
    if (emp?.empresaId) {
      workflowService.getPeriodosEmpresa(emp.empresaId)
        .then(ps => setPeriodos(ps.map(p => ({ value: periodoValueFromIso(p.periodoInicio), label: p.label }))))
        .catch(() => setPeriodos([]));
    } else {
      setPeriodos([]);
    }
  }, [empresaId, empresas]);

  useEffect(() => {
    if (!empresaId || !periodo) {
      setDados({ dividaTecnicaAtual: 0, abertas: 0, herdadas: 0, vencidas: 0, agingMedioDias: 0, itens: [] });
      return;
    }
    setCarregando(true);
    workflowService.listarPendenciasTransversais(Number(empresaId), periodo)
      .then(setDados)
      .catch(() => {})
      .finally(() => setCarregando(false));
  }, [empresaId, periodo]);

  const isReady = !!empresaId && !!periodo;

  const formatData = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
  };

  return <div className='wf-page'>
    <Header title='Pendências Transversais' subtitle='Conciliações ainda abertas cujo período de origem já ficou pra trás — transbordo entre ciclos.' actions={<button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>} />
    <Card>
      <div className='wf-search'>
        <select value={empresaId} onChange={e => { setEmpresaId(e.target.value); setPeriodo(''); }}>
          <option value=''>— Selecione a empresa —</option>
          {empresas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        <select value={periodo} onChange={e => setPeriodo(e.target.value)} disabled={!empresaId || periodos.length === 0}>
          <option value=''>
            {!empresaId ? 'Selecione a empresa primeiro' : periodos.length === 0 ? 'Sem períodos disponíveis' : '— Selecione o período —'}
          </option>
          {periodos.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>
    </Card>
    {!isReady ? (
      <Card><p className='wf-count' style={{ textAlign: 'center', padding: 24 }}>
        {!empresaId ? 'Selecione uma empresa para consultar as pendências transversais.' : 'Selecione o período.'}
      </p></Card>
    ) : <>
    <div className='wf-grid-2'>
      <Card title='Dívida técnica atual' subtitle='Somatório do valor das conciliações transbordadas ainda não resolvidas.'>
        <div className='wf-debt'><strong>{money(dados.dividaTecnicaAtual)}</strong><small>{dados.abertas} conta{dados.abertas !== 1 ? 's' : ''} em aberto além do ciclo de origem</small></div>
      </Card>
      <Card title='Resumo do transbordo'>
        <div className='wf-kpis'>
          <div><span>Abertas</span><strong>{dados.abertas}</strong></div>
          <div><span>Herdadas</span><strong>{dados.herdadas}</strong></div>
          <div><span>Vencidas</span><strong>{dados.vencidas}</strong></div>
          <div><span>Aging médio</span><strong>{Math.round(dados.agingMedioDias)} dias</strong></div>
        </div>
      </Card>
    </div>
    <Card title='Mapa de pendências'>
      <div className='wf-table-wrap'><table className='wf-table'><thead><tr><th>Empresa / Conta</th><th>Ciclo original</th><th>Ciclo atual</th><th>Problema</th><th>Prazo</th><th>Aging</th><th>Status</th></tr></thead><tbody>
        {carregando ? (
          <tr><td colSpan={7} style={{ textAlign: 'center', color: '#a1a5b7', padding: 24 }}>Carregando...</td></tr>
        ) : dados.itens.length === 0 ? (
          <tr><td colSpan={7} style={{ textAlign: 'center', color: '#a1a5b7', padding: 24 }}>Nenhuma pendência transversal — tudo em dia com o ciclo de origem.</td></tr>
        ) : dados.itens.map(item => <tr key={item.concilId}>
          <td><strong>{item.empresa}</strong><span>{item.conta} — {item.descricaoConta}</span></td>
          <td>{item.cicloOriginal}</td>
          <td>{item.cicloAtual}</td>
          <td>{item.problema}</td>
          <td>{formatData(item.prazo)}</td>
          <td>{item.agingDias} dias</td>
          <td><span className={`wf-badge ${item.status === 'VENCIDA' ? 'wf-badge--rejeitada' : 'wf-badge--aguardando_aprovacao'}`}>{item.status === 'VENCIDA' ? 'Vencida' : 'Em tratamento'}</span></td>
        </tr>)}
      </tbody></table></div>
    </Card>
    </>}
  </div>;
};

export const AuditoriaSoxPage: React.FC = () => {
  const history = useHistory();
  const [soxEvents, setSoxEvents] = useState<SoxEvent[]>([]);
  const [selected, setSelected] = useState<SoxEvent | null>(null);
  useEffect(() => { workflowService.listarSox().then(setSoxEvents).catch(() => {}); }, []);
  return <div className='wf-page'><Header title='Auditoria SOX' subtitle='Log imutável de execução, segregação de funções e prova de integridade.' actions={<><button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button><button className='wf-btn wf-btn--primary'>Exportar relatório</button></>} /><Card><div className='wf-table-wrap'><table className='wf-table'><thead><tr><th>Data / Hora</th><th>Executor</th><th>Titular</th><th>Nível</th><th>Ação</th><th>Etapa</th><th>IP</th><th>Hash</th><th /></tr></thead><tbody>{soxEvents.length === 0 ? <tr><td colSpan={9} style={{textAlign:'center',color:'#a1a5b7',padding:24}}>Nenhum evento SOX registrado ainda.</td></tr> : soxEvents.map(e => <tr key={e.id}><td>{e.dataHora}</td><td>{e.executor}</td><td>{e.titular}</td><td>Nível {e.nivel}</td><td>{e.acao}{e.escopo && <small style={{ display: 'block', color: '#7e8299' }}>{e.escopo}</small>}</td><td>{e.etapa}</td><td>{e.ip}</td><td><code>{e.hashAtual}</code></td><td><button className='wf-link' onClick={() => setSelected(e)}>Abrir evento</button></td></tr>)}</tbody></table></div></Card>{selected && <div className='wf-modal-bg'><div className='wf-modal wf-modal--wide'><div className='wf-modal__header'><div><h2>Evento de Auditoria #{selected.id}</h2><p>Registro imutável do histórico SOX.</p></div><button onClick={() => setSelected(null)}>×</button></div><div className='wf-modal__body'><div className='wf-kpis'><div><span>Executor</span><strong>{selected.executor}</strong></div><div><span>Titular</span><strong>{selected.titular}</strong></div><div><span>Ação</span><strong>{selected.acao}</strong></div><div><span>Nível</span><strong>{selected.nivel}</strong></div></div><div className='wf-kpis'><div><span>Data / hora (com fuso)</span><strong>{selected.dataHora}</strong></div><div><span>Afetado</span><strong style={{ fontSize: 12 }}>{selected.escopo || '—'}</strong></div></div><div className='wf-hash'><div><span>Hash anterior</span><code>{selected.hashAnterior}</code></div><b>→</b><div><span>Hash atual</span><code>{selected.hashAtual}</code></div></div><label>Justificativa<textarea readOnly rows={4} value={selected.justificativa} /></label></div><div className='wf-modal__footer'><button className='wf-btn wf-btn--secondary' onClick={() => setSelected(null)}>Fechar</button></div></div></div>}</div>;
};

export { default as GovernancaPage } from './GovernancaPage';

export { default as ConfiguracoesPage } from './ConfiguracoesPage';
